import { DayMode } from '../routine/enums';
import { Equipment } from './enums';

export interface CatalogExercise {
  readonly id: string;
  readonly name: string;
  readonly equipment: Equipment;
  readonly supportsWeight: boolean;
  readonly unit: 'reps' | 'minutes' | 'seconds';
  readonly targetValue: number;
  readonly instructions: string;
  /** Equipment-free alternative offered as a replacement; absent when already equipment-free. */
  readonly replacementId?: string;
  readonly highIntensity: boolean;
}

const CATALOG: readonly CatalogExercise[] = [
  {
    id: 'treadmill-walk',
    name: 'Marche rapide sur tapis',
    equipment: Equipment.treadmill,
    supportsWeight: false,
    unit: 'minutes',
    targetValue: 15,
    instructions: 'Marche à un rythme soutenu, sans forcer l’allure.',
    replacementId: 'jumping-jacks',
    highIntensity: false,
  },
  {
    id: 'stationary-bike',
    name: 'Vélo stationnaire',
    equipment: Equipment.bike,
    supportsWeight: false,
    unit: 'minutes',
    targetValue: 15,
    instructions: 'Pédale à une cadence régulière, résistance modérée.',
    replacementId: 'jumping-jacks',
    highIntensity: false,
  },
  {
    id: 'bench-press',
    name: 'Développé couché',
    equipment: Equipment.bench,
    supportsWeight: true,
    unit: 'reps',
    targetValue: 12,
    instructions: 'Choisis un poids que tu peux contrôler sur toute l’amplitude.',
    replacementId: 'push-ups',
    highIntensity: false,
  },
  {
    id: 'dumbbell-row',
    name: 'Rowing haltère',
    equipment: Equipment.dumbbells,
    supportsWeight: true,
    unit: 'reps',
    targetValue: 12,
    instructions: 'Dos plat, tire l’haltère vers la hanche sans à-coup.',
    replacementId: 'plank',
    highIntensity: false,
  },
  {
    id: 'goblet-squat',
    name: 'Squat gobelet',
    equipment: Equipment.dumbbells,
    supportsWeight: true,
    unit: 'reps',
    targetValue: 15,
    instructions: 'Descends jusqu’à ce que les cuisses soient parallèles au sol si possible.',
    replacementId: 'bodyweight-squats',
    highIntensity: false,
  },
  {
    id: 'dumbbell-shoulder-press',
    name: 'Développé épaules',
    equipment: Equipment.dumbbells,
    supportsWeight: true,
    unit: 'reps',
    targetValue: 10,
    instructions: 'Pousse les haltères au-dessus de la tête sans cambrer le dos.',
    replacementId: 'push-ups',
    highIntensity: false,
  },
  {
    id: 'push-ups',
    name: 'Pompes',
    equipment: Equipment.none,
    supportsWeight: false,
    unit: 'reps',
    targetValue: 10,
    instructions: 'Genoux au sol si nécessaire, amplitude complète.',
    highIntensity: false,
  },
  {
    id: 'bodyweight-squats',
    name: 'Squats au poids du corps',
    equipment: Equipment.none,
    supportsWeight: false,
    unit: 'reps',
    targetValue: 15,
    instructions: 'Descends à ton amplitude confortable, rythme régulier.',
    highIntensity: false,
  },
  {
    id: 'jumping-jacks',
    name: 'Jumping jacks',
    equipment: Equipment.none,
    supportsWeight: false,
    unit: 'minutes',
    targetValue: 5,
    instructions: 'Rythme modéré, respiration régulière.',
    highIntensity: true,
  },
  {
    id: 'plank',
    name: 'Gainage',
    equipment: Equipment.none,
    supportsWeight: false,
    unit: 'seconds',
    targetValue: 30,
    instructions: 'Corps aligné, respiration continue.',
    highIntensity: false,
  },
  {
    id: 'guided-stretch',
    name: 'Étirements guidés',
    equipment: Equipment.none,
    supportsWeight: false,
    unit: 'minutes',
    targetValue: 10,
    instructions: 'Étire chaque groupe musculaire sans à-coup ni douleur.',
    highIntensity: false,
  },
  {
    id: 'breathing-walk',
    name: 'Marche respirée',
    equipment: Equipment.none,
    supportsWeight: false,
    unit: 'minutes',
    targetValue: 10,
    instructions: 'Marche à un rythme confortable en respirant lentement.',
    highIntensity: false,
  },
];

export function findCatalogExercise(id: string): CatalogExercise | undefined {
  return CATALOG.find((exercise) => exercise.id === id);
}

/**
 * MVP-bounded day-based sessions (SPORT_ENGINE.md, product/L0_DECISIONS_CLOSED.md §4):
 * normal uses the full local catalog, busy shortens it, travel replaces every equipped
 * exercise with its equipment-free alternative, recovery excludes high-intensity work,
 * and difficult contains only ten minutes of accessible movement. Elevation demands the
 * same full engagement as normal; moment-decisif and imprevu tighten focus like difficult
 * and travel respectively, and transition stays gentle like recovery.
 */
export function buildSessionForMode(dayMode: DayMode): readonly CatalogExercise[] {
  switch (dayMode) {
    case DayMode.difficult:
    case DayMode.momentDecisif:
      return [mustFind('breathing-walk')];
    case DayMode.recovery:
    case DayMode.transition:
      return [mustFind('guided-stretch'), mustFind('breathing-walk')].filter(
        (exercise) => !exercise.highIntensity,
      );
    case DayMode.travel:
    case DayMode.imprevu:
      return ['jumping-jacks', 'push-ups', 'bodyweight-squats', 'plank', 'guided-stretch'].map(
        mustFind,
      );
    case DayMode.busy:
      return ['treadmill-walk', 'goblet-squat', 'plank'].map(mustFind);
    case DayMode.normal:
    case DayMode.elevation:
      return [
        'treadmill-walk',
        'bench-press',
        'dumbbell-row',
        'goblet-squat',
        'dumbbell-shoulder-press',
        'plank',
      ].map(mustFind);
  }
}

function mustFind(id: string): CatalogExercise {
  const exercise = findCatalogExercise(id);
  if (!exercise) throw new Error(`Unknown catalog exercise: ${id}`);
  return exercise;
}
